"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveSeriesPoints = resolveSeriesPoints;
exports.buildDivisionScopedPointsRankMap = buildDivisionScopedPointsRankMap;
exports.applySeriesPointsToRows = applySeriesPointsToRows;
const seriesPointsDebug_1 = require("./seriesPointsDebug");
// ─── Core lookup ─────────────────────────────────────────────────────────────
/**
 * Resolves the series points for a given rank and optional division.
 * Falls back to global points (with scale 1) when no division override is found.
 * Returns 0 when the rank is outside all configured ranges (WD, DQ, not yet ranked).
 */
function resolveSeriesPoints(rank, division, seriesPoints) {
    if (!rank || rank <= 0)
        return 0;
    const rangeIdx = seriesPoints.ranges.findIndex((r) => rank >= r.startPlace && rank <= (r.endPlace ?? r.startPlace));
    if (rangeIdx === -1)
        return 0;
    const basePoints = seriesPoints.ranges[rangeIdx].points;
    const divEntry = division
        ? seriesPoints.divisionPoints.find((d) => d.division === division)
        : undefined;
    let result;
    if (!divEntry || divEntry.mode === 'global-scaled') {
        const scale = divEntry?.scale ?? 1;
        result = Math.round(basePoints * scale);
    }
    else {
        result = divEntry.customPoints[rangeIdx] ?? 0;
    }
    (0, seriesPointsDebug_1.logSeriesPoints)('resolve', { rank, division, rangeIdx, basePoints, scale: divEntry?.scale ?? 1, result });
    return result;
}
// ─── Division-scoped points rank ─────────────────────────────────────────────
function compareLeaderboardScore(a, b, isStableford) {
    if (a.holesCompleted === 0 && b.holesCompleted === 0)
        return 0;
    if (a.holesCompleted === 0)
        return 1;
    if (b.holesCompleted === 0)
        return -1;
    if (a.widthdrawn || a.disqualified)
        return 1;
    if (b.widthdrawn || b.disqualified)
        return -1;
    if (isStableford) {
        if (a.totalPoints !== b.totalPoints)
            return b.totalPoints - a.totalPoints;
    }
    else if (a.toPar !== b.toPar) {
        return a.toPar - b.toPar;
    }
    return b.holesCompleted - a.holesCompleted;
}
function sameLeaderboardScore(a, b, isStableford) {
    if (a.holesCompleted === 0 || b.holesCompleted === 0) {
        return a.holesCompleted === b.holesCompleted;
    }
    if (a.widthdrawn || a.disqualified || b.widthdrawn || b.disqualified)
        return false;
    if (isStableford)
        return a.totalPoints === b.totalPoints;
    return a.toPar === b.toPar;
}
function isStrictlyBetterInDivision(a, reference, isStableford) {
    if (a.widthdrawn || a.disqualified || reference.widthdrawn || reference.disqualified)
        return false;
    if (a.holesCompleted === 0 || reference.holesCompleted === 0)
        return false;
    if (isStableford)
        return (a.totalPoints ?? 0) > (reference.totalPoints ?? 0);
    return (a.toPar ?? 0) < (reference.toPar ?? 0);
}
/**
 * Assigns division-wide finishing places for series points within one division.
 * Playoff / admin overrides are recomputed against the full division peer set so
 * flight-local or whole-field finalRank values cannot inflate place points.
 */
function assignDivisionPointsRanks(divisionRows, isStableford, map) {
    const adminRanks = divisionRows
        .filter((item) => item.adminRank && item.adminRank > 0)
        .map((item) => item.adminRank);
    const sorted = [...divisionRows].sort((a, b) => compareLeaderboardScore(a, b, isStableford));
    const acc = [];
    for (const item of sorted) {
        const divisionPeersInAcc = acc;
        const divisionLocalIndex = divisionPeersInAcc.length;
        const prevInDivision = divisionPeersInAcc.length > 0
            ? divisionPeersInAcc[divisionPeersInAcc.length - 1]
            : null;
        let calculatedRank;
        if (item.adminRank && item.adminRank > 0) {
            const tiedPeers = divisionRows.filter((p) => p.holesCompleted > 0 &&
                !p.widthdrawn &&
                !p.disqualified &&
                sameLeaderboardScore(p, item, isStableford));
            const betterCount = divisionRows.filter((p) => isStrictlyBetterInDivision(p, item, isStableford)).length;
            const orderedTied = [...tiedPeers].sort((a, b) => {
                const ar = a.adminRank > 0 ? a.adminRank : 9999;
                const br = b.adminRank > 0 ? b.adminRank : 9999;
                if (ar !== br)
                    return ar - br;
                return compareLeaderboardScore(a, b, isStableford);
            });
            const idx = orderedTied.findIndex((p) => p._id === item._id);
            calculatedRank = betterCount + 1 + (idx >= 0 ? idx : 0);
        }
        else if (prevInDivision &&
            ((item.holesCompleted > 0 &&
                prevInDivision.holesCompleted > 0 &&
                sameLeaderboardScore(item, prevInDivision, isStableford)) ||
                (item.holesCompleted === 0 && prevInDivision.holesCompleted === 0))) {
            calculatedRank = prevInDivision.rank;
        }
        else {
            const divLocalPos = divisionLocalIndex + 1;
            let candidate = divLocalPos;
            while (adminRanks.includes(candidate)) {
                candidate++;
            }
            calculatedRank = candidate;
        }
        const rankedItem = { ...item, rank: calculatedRank };
        if (item._id != null) {
            map[item._id.toString()] = calculatedRank;
        }
        acc.push(rankedItem);
    }
}
/**
 * Builds a map of eventScore _id → division-scoped finishing place for series points.
 * When divisions are configured, rank is always relative to the player's division
 * (never whole-field or flight-local). Without divisions, uses existing whole-field rank.
 */
function buildDivisionScopedPointsRankMap(leaderboardData, hasDivisions, isStableford) {
    const map = {};
    if (!leaderboardData?.length)
        return map;
    if (!hasDivisions) {
        for (const row of leaderboardData) {
            if (row._id != null) {
                map[row._id.toString()] = row.rank ?? 0;
            }
        }
        return map;
    }
    const byDivision = new Map();
    for (const row of leaderboardData) {
        const div = row.playerInfo?.[0]?.division ?? '';
        if (!byDivision.has(div))
            byDivision.set(div, []);
        byDivision.get(div).push(row);
    }
    Array.from(byDivision.values()).forEach((divisionRows) => {
        assignDivisionPointsRanks(divisionRows, isStableford, map);
    });
    return map;
}
// ─── Row annotation ───────────────────────────────────────────────────────────
/**
 * Annotates each row in a ranked array with `seriesPointsEarned`.
 *
 * @param rows         Array of ranked leaderboard/team rows.
 * @param seriesPoints The series points config from the EventGroup.
 * @param rankKey      Which rank field to use ('rank' for individual, 'teamRank' for teams).
 * @param getDivision  Optional callback to extract the division from a row.
 *                     Defaults to `row.playerInfo?.[0]?.division` (individual rows).
 *                     For team rows pass: `(row) => row.eventScores?.[0]?.playerInfo?.[0]?.division`
 */
function applySeriesPointsToRows(rows, seriesPoints, rankKey = 'rank', getDivision) {
    return rows.map((row) => ({
        ...row,
        seriesPointsEarned: resolveSeriesPoints(row[rankKey], getDivision ? getDivision(row) : (row.playerInfo?.[0]?.division ?? null), seriesPoints),
    }));
}
